import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Collapse, IconButton } from '@mui/material';
import React, { FC, useEffect, useState } from 'react';
import { vacancyService } from '../../../../../services/vacancy.service';
import type { VacancyQuestionResponse } from '../../../../../services/types/vacancy.types';
import Loading from '../../../../common/Loading';
import QuestionBooleanIcon from '../../../../common/icons/QuestionBooleanIcon';
import QuestionCurrencyIcon from '../../../../common/icons/QuestionCurrencyIcon';
import QuestionDateIcon from '../../../../common/icons/QuestionDateIcon';
import QuestionDecimalIcon from '../../../../common/icons/QuestionDecimalIcon';
import QuestionNumberIcon from '../../../../common/icons/QuestionNumberIcon';
import QuestionTextIcon from '../../../../common/icons/QuestionTextIcon';

const ANSWER_TYPE_ICON: Record<VacancyQuestionResponse['answerType'], React.ReactElement> = {
  text: <QuestionTextIcon />,
  boolean: <QuestionBooleanIcon />,
  number: <QuestionNumberIcon />,
  currency: <QuestionCurrencyIcon />,
  date: <QuestionDateIcon />,
  decimal: <QuestionDecimalIcon />,
};

const LEVEL_ORDER = [0, 1, 5] as const;

const LEVEL_LABELS: Record<(typeof LEVEL_ORDER)[number], string> = {
  0: 'Screening Questions',
  1: '1st Interview',
  5: 'Final Interview',
};

function groupAndSort(questions: VacancyQuestionResponse[]) {
  const grouped: {
    0: VacancyQuestionResponse[];
    1: VacancyQuestionResponse[];
    5: VacancyQuestionResponse[];
  } = { 0: [], 1: [], 5: [] };

  questions.forEach((q) => {
    const level = q.questionLevel;
    if (level === 0 || level === 1 || level === 5) {
      grouped[level].push(q);
    }
  });

  LEVEL_ORDER.forEach((level) => {
    grouped[level].sort((a, b) => a.sortOrder - b.sortOrder);
  });

  return grouped;
}

interface ViewVacancyInterviewQuestionsProps {
  vacancyId: number;
}

const ViewVacancyInterviewQuestions: FC<ViewVacancyInterviewQuestionsProps> = ({ vacancyId }) => {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [questions, setQuestions] = useState<VacancyQuestionResponse[] | null>(null);

  useEffect(() => {
    if (!expanded || questions !== null) return;

    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await vacancyService.getVacancyQuestions(vacancyId);
        if (!cancelled) {
          setQuestions(res.data.data || []);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setError('Failed to load interview questions.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [expanded, vacancyId, questions]);

  const grouped = questions ? groupAndSort(questions) : null;
  const hasAny = grouped && LEVEL_ORDER.some((level) => grouped[level].length > 0);

  return (
    <Box className="view-vacancy-section" sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '10px' }}>
        <div className="job-description-title">Interview Questions</div>
        <IconButton
          type="button"
          data-testid="interview-questions-toggle"
          onClick={() => setExpanded((prev) => !prev)}
          className="jd-expand-collapse-button"
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse interview questions' : 'Expand interview questions'}
        >
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        {loading && (
          <Box className="interview-questions-loading" sx={{ py: 2 }}>
            <Loading />
          </Box>
        )}
        {!loading && error && (
          <Box className="interview-questions-error" sx={{ py: 1 }}>
            {error}
          </Box>
        )}
        {!loading && !error && grouped && !hasAny && (
          <Box className="interview-questions-empty" sx={{ py: 1 }}>
            No interview questions configured for this vacancy.
          </Box>
        )}
        {!loading && !error && grouped && hasAny && (
          <Box className="interview-questions-body" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {LEVEL_ORDER.map((level) => {
              const rows = grouped[level];
              if (rows.length === 0) return null;

              return (
                <Box key={level} className="interview-questions-stage">
                  <div className="interview-questions-stage-title">{LEVEL_LABELS[level]}</div>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1.5 }}>
                    {rows.map((q, index) => (
                      <Box key={q.id} className="interview-questions-row">
                        <span className="interview-questions-row-number">{index + 1}</span>
                        <span className="interview-questions-row-icon">
                          {ANSWER_TYPE_ICON[q.answerType] ?? <QuestionTextIcon />}
                        </span>
                        <span className="interview-questions-row-text">{q.question}</span>
                      </Box>
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Collapse>
    </Box>
  );
};

export default ViewVacancyInterviewQuestions;
